import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import {
  LayoutDashboard,
  Users,
  Search,
  BarChart3,
  FileCheck,
  LogOut,
  Globe,
  UserCog,
  UserCircle,
  BookOpen,
  Sparkles,
  Sliders,
  Sun,
  Moon,
  ScanSearch,
} from "lucide-react";
import { useAuth } from "../lib/auth";

// Theme management — persists to localStorage, defaults to light (same as
// current experience). Set on <html> so CSS attribute selectors in
// index.css can re-skin globally without touching every component.
const THEME_KEY = "rankos.theme";
function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}
function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") root.setAttribute("data-theme", "dark");
  else root.removeAttribute("data-theme");
}

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/tools/keyword-research", label: "Keyword Research", icon: Search, testid: "nav-keyword-research" },
  { to: "/tools/serp", label: "SERP Analysis", icon: BarChart3, testid: "nav-serp" },
  { to: "/tools/on-page", label: "On-Page Audit", icon: FileCheck, testid: "nav-on-page" },
  { to: "/tools/ai-search", label: "AI Search", icon: Sparkles, testid: "nav-ai-search" },
  { to: "/tools/site-xray", label: "Site X-Ray", icon: ScanSearch, testid: "nav-site-xray", isNew: true },
  { to: "/knowledge-base", label: "Knowledge Base", icon: BookOpen, testid: "nav-knowledge-base" },
  { to: "/changelog", label: "What's new", icon: Sparkles, testid: "nav-changelog" },
];

// Settings group at the bottom of the sidebar — separated visually from the
// main nav so admin-only items don't compete with the daily-work links above.
const settingsItems = [
  { to: "/account", label: "Account", icon: UserCircle, testid: "nav-account", admin: false },
  { to: "/settings", label: "App settings", icon: Sliders, testid: "nav-settings", admin: true },
  { to: "/users", label: "Users", icon: UserCog, testid: "nav-users", admin: true },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  // Live status pill — polls /api/status.json every 5 min. Falls back to
  // a neutral "checking…" state on first paint or when the network errors,
  // so the header never shows misleading data. `status` is "ok" | "degraded"
  // per the backend; "unknown" is our local sentinel for pre-fetch / errors.
  const [status, setStatus] = useState({ state: "unknown", uptime: null });
  // Theme toggle — mounts in the top-right header next to the API status
  // pill. Default `light` matches the current product and keeps the first-
  // visit experience unchanged.
  const [theme, setTheme] = useState(() => readStoredTheme());
  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(THEME_KEY, theme); } catch {}
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // "New feature" pulse state — once the user clicks a link flagged as
  // isNew, we remember it in localStorage and stop pulsing. Keyed by `to`
  // so adding future new features is just a matter of setting isNew:true.
  const [seenNew, setSeenNew] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("rankos.seen_new") || "[]"));
    } catch {
      return new Set();
    }
  });
  const markSeen = (to) => {
    if (seenNew.has(to)) return;
    const next = new Set(seenNew);
    next.add(to);
    setSeenNew(next);
    try { localStorage.setItem("rankos.seen_new", JSON.stringify([...next])); } catch {}
  };
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/status.json");
        if (!cancelled) setStatus({ state: data.status || "unknown", uptime: data.uptime_human });
      } catch {
        if (!cancelled) setStatus({ state: "unknown", uptime: null });
      }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const statusMeta = {
    ok:       { color: "#38b2ac", label: "API OK",         dot: "bg-[#38b2ac]" },
    degraded: { color: "#dd6b20", label: "API degraded",   dot: "bg-[#dd6b20] animate-pulse" },
    unknown:  { color: "#a0aec0", label: "API checking…",  dot: "bg-[#a0aec0]" },
  }[status.state];

  return (
    <div className="min-h-screen flex bg-[#f7fafc] text-[#1a202c]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 p-5 flex flex-col">
        <div
          className="px-3 h-14 flex items-center gap-3 cursor-pointer"
          onClick={() => navigate("/")}
          data-testid="brand-logo"
        >
          <div className="brand-icon">
            <Globe className="h-4 w-4" strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-display text-sm leading-tight">
              rank<span className="text-[#38b2ac]">.</span>os
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aec0]">
              SEO Dashboard
            </div>
          </div>
        </div>

        <div className="my-4 h-px bg-gradient-to-r from-transparent via-[#e2e8f0] to-transparent" />

        <nav className="flex-1 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const showNew = item.isNew && !seenNew.has(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={() => item.isNew && markSeen(item.to)}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                    isActive
                      ? "bg-white shadow-[0_4px_12px_-4px_rgba(45,55,72,0.08)] text-[#1a202c]"
                      : "text-[#4a5568] hover:bg-white/60"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${
                        isActive
                          ? "bg-brand text-white shadow-[0_4px_10px_-2px_rgba(56,178,172,0.45)]"
                          : "bg-white text-[#4a5568] border border-[#e2e8f0]"
                      }`}
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.8} />
                    </div>
                    <span className="text-sm font-medium flex-1">{item.label}</span>
                    {showNew && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-[#2c7a7b] bg-[#4fd1c5]/15 border border-[#4fd1c5]/40 px-1.5 py-0.5 rounded-full"
                        data-testid={`${item.testid}-new-badge`}
                        title="New feature"
                      >
                        <span className="relative inline-flex h-1.5 w-1.5">
                          <span className="absolute inset-0 rounded-full bg-[#4fd1c5] opacity-75 animate-ping" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#4fd1c5]" />
                        </span>
                        New
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Settings group — pinned to the bottom of the sidebar */}
        <div className="mt-4 mb-2 px-3 text-[10px] uppercase tracking-widest text-[#cbd5e0] font-bold">Settings</div>
        <nav className="space-y-1">
          {settingsItems
            .filter((item) => !item.admin || isAdmin)
            .map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
                      isActive
                        ? "bg-white shadow-[0_4px_12px_-4px_rgba(45,55,72,0.08)] text-[#1a202c]"
                        : "text-[#4a5568] hover:bg-white/60"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div
                        className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${
                          isActive
                            ? "bg-brand text-white shadow-[0_4px_10px_-2px_rgba(56,178,172,0.45)]"
                            : "bg-white text-[#4a5568] border border-[#e2e8f0]"
                        }`}
                      >
                        <Icon className="h-4 w-4" strokeWidth={1.8} />
                      </div>
                      <span className="text-sm font-medium">{item.label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
        </nav>

        {/* User card */}
        <div className="mt-4 flex items-center gap-3 px-2 py-2.5 rounded-xl">
          <div className="h-9 w-9 rounded-lg bg-brand text-white flex items-center justify-center text-xs font-semibold">
            {user?.name?.[0]?.toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate text-[#1a202c]" data-testid="user-name">
              {user?.name || "Admin"}
            </div>
            <div className="text-[10px] text-[#718096] truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="h-7 w-7 flex items-center justify-center rounded-lg text-[#a0aec0] hover:text-[#f56565] hover:bg-[#fed7d7]/40"
            title="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 px-8 flex items-center justify-between bg-transparent sticky top-0 z-30 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-[11px] text-[#718096]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#48bb78] pulse-dot" />
              <span className="font-mono-tight">live · dataforseo</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${API_BASE}/status`}
              target="_blank"
              rel="noreferrer"
              title={
                status.state === "ok"
                  ? "All systems operational — open live status dashboard"
                  : status.state === "degraded"
                  ? "Some subsystems reporting issues — click to see details"
                  : "Fetching current status…"
              }
              data-testid="header-api-status-link"
              className="hidden md:inline-flex items-center gap-2 text-[11px] text-[#718096] hover:text-[#2c7a7b] font-mono-tight transition-colors group"
            >
              <span
                className={`inline-block h-2 w-2 rounded-full ${statusMeta.dot}`}
                data-testid="header-api-status-dot"
              />
              <span data-testid="header-api-status-label">
                {statusMeta.label}
                {status.uptime && (
                  <span className="text-[#a0aec0] group-hover:text-[#4fd1c5]"> · {status.uptime} up</span>
                )}
              </span>
            </a>
            <button
              type="button"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-label="Toggle theme"
              className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-white hover:bg-[#f7fafc] border border-[#e2e8f0] text-[#718096] hover:text-[#2c7a7b] transition-all shadow-[0_1px_2px_0_rgba(45,55,72,0.04)]"
            >
              {theme === "dark" ? (
                <Sun className="h-3.5 w-3.5" strokeWidth={2.2} data-testid="theme-icon-sun" />
              ) : (
                <Moon className="h-3.5 w-3.5" strokeWidth={2.2} data-testid="theme-icon-moon" />
              )}
            </button>
          </div>
        </header>

        <main className="flex-1 px-8 pb-8 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
